import type { CartLine } from "@/graphql/storefront/generated";
import { isMaticFamilyVariantId } from "@/utils/maticFamily";

type OpenAIContentItem = {
  id: string;
  name: string;
  content_type: "page" | "product";
  quantity?: number;
  amount?: number;
};

/** OpenAI's `contents` shape; amounts are integer minor units (cents). */
type OpenAIContentsData = {
  type: "contents";
  amount?: number;
  currency?: string;
  contents: OpenAIContentItem[];
};

type OpenAIEventMap = {
  page_viewed: OpenAIContentsData;
  items_added: OpenAIContentsData;
};

/**
 * OpenAI Ads standard events. `window.oaiq` only exists once the
 * consent-gated pixel has loaded, so a declined visitor sends nothing.
 * `buildData` runs inside the try, so a payload that can't be built never
 * reaches the caller (a cart add, a navigation).
 */
export const trackOpenAI = <Name extends keyof OpenAIEventMap>(
  name: Name,
  buildData: () => OpenAIEventMap[Name] | null,
): void => {
  if (typeof window === "undefined" || typeof window.oaiq !== "function") {
    return;
  }
  try {
    const data = buildData();
    if (data === null) return;
    window.oaiq("measure", name, data);
  } catch (error) {
    console.error(`Failed to send OpenAI ${name}:`, error);
  }
};

const toCents = (amount: string): number => Math.round(Number(amount) * 100);

/**
 * `items_added` for the robots among the added lines, null when there are
 * none: the campaigns optimise for robot sales, so bags and accessories
 * would only teach them the wrong buyer.
 */
export const robotItemsAdded = (
  added: { line: Pick<CartLine, "merchandise">; quantity: number }[],
): OpenAIContentsData | null => {
  const robots = added.filter(({ line }) =>
    isMaticFamilyVariantId(line.merchandise.id),
  );
  const [first] = robots;
  if (first === undefined) return null;
  return {
    type: "contents",
    amount: robots.reduce(
      (sum, { line, quantity }) =>
        sum + toCents(line.merchandise.price.amount) * quantity,
      0,
    ),
    currency: first.line.merchandise.price.currencyCode,
    contents: robots.map(({ line, quantity }) => ({
      id: line.merchandise.product.id.split("/").pop() ?? "",
      name: line.merchandise.product.title,
      content_type: "product",
      quantity,
      amount: toCents(line.merchandise.price.amount),
    })),
  };
};
